import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { InvoicePaymentRow, InvoicePaymentsView } from "../../api/types";
import { calendarDayText, money } from "../../i18n/intl";
import {
  paymentMethodLabel,
  paymentStateLabel,
  paymentStateTone,
} from "../../utils/invoicePaymentText";
import { ReasonModal } from "../ui/ReasonModal";
import { RecordInvoicePaymentModal } from "./RecordInvoicePaymentModal";

/**
 * G-121 S4 (OD-146/OD-147): a factura's paid state, on its own page — owner
 * only (the parent renders it for the owner and for facturas only; the
 * server refuses anyone else). Paid when ordered unless marked "not paid
 * yet"; each payment listed, removed ones struck through with who and why.
 */
export function InvoicePaymentsBlock({
  invoiceId,
  onChanged,
}: {
  invoiceId: string;
  /** Called after any change, so a surrounding list can refresh. */
  onChanged?: () => void;
}) {
  const [view, setView] = useState<InvoicePaymentsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [removing, setRemoving] = useState<InvoicePaymentRow | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.getInvoicePayments(invoiceId));
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load payments");
    }
  }, [invoiceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const changed = (next: InvoicePaymentsView) => {
    setView(next);
    onChanged?.();
  };

  const setAwaiting = async (awaitingPayment: boolean) => {
    setBusy(true);
    setError(null);
    try {
      changed(await api.setInvoiceAwaitingPayment(invoiceId, awaitingPayment));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not change the paid state");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (reason: string | undefined) => {
    if (!removing || !reason) return;
    setBusy(true);
    setRemoveError(null);
    try {
      changed(await api.removeInvoicePayment(invoiceId, removing.id, reason));
      setRemoving(null);
    } catch (err: unknown) {
      setRemoveError(err instanceof Error ? err.message : "Could not remove the payment");
    } finally {
      setBusy(false);
    }
  };

  if (!view) {
    return (
      <div className="inv-payments">
        <strong>Payment</strong>
        {error ? <div className="alert error">{error}</div> : <div className="muted">Loading…</div>}
      </div>
    );
  }

  if (view.copyOf) {
    return (
      <div className="inv-payments">
        <strong>Payment</strong>
        <div className="alert warning">
          This upload is a copy of a factura already in the app; it is not counted and takes no payments.{" "}
          <Link to={`/app/invoices/${view.copyOf.id}`}>Open the factura that counts</Link>
        </div>
      </div>
    );
  }

  const credited = view.creditedEur === null || Number(view.creditedEur) !== 0;
  return (
    <div className="inv-payments">
      <div className="inv-payments-head">
        <strong>Payment</strong>
        <span className={`badge ${paymentStateTone(view.state)}`}>{paymentStateLabel(view.state)}</span>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      <div className="inv-money-grid">
        <div>
          <small className="muted">Total (with IVA)</small>
          <div>{money(view.grandTotalEur)}</div>
        </div>
        {/* G-173: its credit notes take off what is to pay. */}
        {credited ? (
          <>
            <div>
              <small className="muted">Credited</small>
              <div>{money(view.creditedEur)}</div>
            </div>
            <div>
              <small className="muted">To pay</small>
              <div>{money(view.toPayEur)}</div>
            </div>
          </>
        ) : null}
        <div>
          <small className="muted">Paid</small>
          <div>{money(view.paidEur)}</div>
        </div>
        <div>
          <small className="muted">Still owed</small>
          <div>{money(view.owedEur)}</div>
        </div>
        {view.paidOverEur !== null && Number(view.paidOverEur) > 0 ? (
          <div>
            <small className="muted">Paid over</small>
            <div>{money(view.paidOverEur)}</div>
          </div>
        ) : null}
      </div>
      <p className="muted inv-payments-note">
        {view.awaitingPayment
          ? `Marked not paid yet${view.awaitingSetBy ? ` by ${view.awaitingSetBy.fullName}` : ""}. Owed = ${credited ? "to pay" : "total"} − payments below.`
          : "You pay when ordering, so a factura counts as paid unless you mark it not paid yet."}
      </p>
      <div className="button-row">
        {view.awaitingPayment ? (
          <button type="button" className="button tinted" disabled={busy} onClick={() => void setAwaiting(false)}>
            It was paid when ordered
          </button>
        ) : (
          <button type="button" className="button tinted" disabled={busy} onClick={() => void setAwaiting(true)}>
            Mark not paid yet
          </button>
        )}
        <button type="button" className="button" disabled={busy} onClick={() => setRecording(true)}>
          Record a payment
        </button>
      </div>
      {view.payments.length > 0 ? (
        <ul className="inv-payments-list">
          {view.payments.map((p) => (
            <li key={p.id} className={`inv-payment-row${p.removedAt ? " is-removed" : ""}`}>
              <span className="inv-payment-main">
                <strong>{money(p.amountEur)}</strong> · {calendarDayText(p.paidOn)} · {paymentMethodLabel(p.method)}
                {p.note ? <span className="muted"> · {p.note}</span> : null}
              </span>
              <span className="muted inv-payment-meta">
                {p.removedAt
                  ? `Removed by ${p.removedBy?.fullName ?? "—"}: ${p.removeReason ?? ""}`
                  : `Recorded by ${p.recordedBy.fullName}`}
              </span>
              {p.removedAt ? null : (
                <button
                  type="button"
                  className="button plain"
                  disabled={busy}
                  onClick={() => {
                    setRemoveError(null);
                    setRemoving(p);
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {recording ? (
        <RecordInvoicePaymentModal
          invoiceId={invoiceId}
          suggestedAmountEur={view.awaitingPayment ? view.owedEur : (view.toPayEur ?? view.grandTotalEur)}
          onSaved={(next) => {
            setRecording(false);
            changed(next);
          }}
          onClose={() => setRecording(false)}
        />
      ) : null}
      {removing ? (
        <ReasonModal
          title="Remove this payment?"
          message={`${money(removing.amountEur)} on ${calendarDayText(removing.paidOn)}. The record stays, struck through, with your reason.`}
          reasonMode="required"
          reasonLabel="Why"
          confirmLabel="Remove payment"
          destructive
          busy={busy}
          error={removeError}
          onConfirm={remove}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
